//core
import React, { useEffect, useContext, useMemo, useRef } from "react";
import arePropsEqual from "react-fast-compare";
import { useTranslation } from "react-i18next";
import { Tooltip, useMediaQuery } from "@mui/material";
import { useSelector } from "react-redux";

//styles
import { useStyles } from "./styles";

//routes
import routes from "../../../../../navigation/routes";

//store
import { useAgents as useAdminAgents } from "../../../../../store/admin/agents/useAgents";
import { useAgents } from "../../../../../store/agent/agents/useAgents";
import { useUI } from "../../../../../store/common/ui/useUI";
import { useUser } from "../../../../../store/common/user/useUser";

//selectors
import {
  selectExternalPlayerProfileData as selectAdmExternalPlayerProfileData,
  selectExternalPlayerProfileError as selectAdmExternalPlayerProfileError,
} from "../../../../../store/admin/agents/selectors";
import {
  selectExternalPlayerProfileData,
  selectExternalPlayerProfileError,
} from "../../../../../store/agent/agents/selectors";

//context
import { TableContext } from "../../../../admin/Reports/AgentStatisticsReport/components/DesktopReport/TableContext";

interface IClickableExternalPlayerIdProps {
  value: string | number | null;
}

const ClickableExternalPlayerId = ({
  value = "",
}: IClickableExternalPlayerIdProps) => {
  const styles = useStyles();
  const mobile = useMediaQuery("(max-width:767px)");
  const { t } = useTranslation();
  const { setGeneralSnackbar } = useUI();
  const { user } = useUser();
  const context = useContext(TableContext);

  const { getExternalPlayerProfile, clearExternalPlayerProfile } = useAgents();
  const adminAgents = useAdminAgents();

  const isRequestInitiator = useRef(false);

  const isAdmin = user.user.namespace === "admin";

  const playerProfileData = useSelector(
    isAdmin
      ? selectAdmExternalPlayerProfileData
      : selectExternalPlayerProfileData
  );
  const playerProfileError = useSelector(
    isAdmin
      ? selectAdmExternalPlayerProfileError
      : selectExternalPlayerProfileError
  );

  const { getProfile, clearProfile } = useMemo(() => {
    if (isAdmin) {
      return {
        getProfile: adminAgents.getExternalPlayerProfile,
        clearProfile: adminAgents.clearExternalPlayerProfile,
      };
    }
    return {
      getProfile: getExternalPlayerProfile,
      clearProfile: clearExternalPlayerProfile,
    };
  }, [
    isAdmin,
    adminAgents,
    getExternalPlayerProfile,
    clearExternalPlayerProfile,
  ]);

  useEffect(() => {
    if (playerProfileError) {
      setGeneralSnackbar({
        open: true,
        type: "warning",
        message: "error",
        messageKey: t(`${playerProfileError.error}`),
      });
      clearProfile();
      isRequestInitiator.current = false;
    }
  }, [playerProfileError, clearProfile, setGeneralSnackbar, t]);

  useEffect(() => {
    if (playerProfileData && isRequestInitiator.current) {
      let url: string;

      if (isAdmin) {
        const baseRoute = context?.toggleAllStats
          ? routes.admin.agent.fullPlayers.playerAccount.root
          : routes.admin.agent.playerAccount.root;
        url = `${baseRoute}/${playerProfileData.id}`;
      } else {
        url = `${routes.agent.agentsList.playerAccount.root}/${playerProfileData.id}`;
      }

      window.open(url, "_blank", "noopener,noreferrer");
      clearProfile();
      isRequestInitiator.current = false;
    }
  }, [clearProfile, context?.toggleAllStats, isAdmin, playerProfileData]);

  const handleClick = async () => {
    if (!value) return;
    isRequestInitiator.current = true;
    getProfile({ playerId: value });
  };

  return (
    <>
      {value ? (
        mobile ? (
          <span className={styles.text} onClick={handleClick}>
            {value}
          </span>
        ) : (
          <Tooltip title={value}>
            <span className={styles.text} onClick={handleClick}>
              {value}
            </span>
          </Tooltip>
        )
      ) : (
        "—"
      )}
    </>
  );
};

export default React.memo(ClickableExternalPlayerId, arePropsEqual);
